import React, { useEffect, useState } from 'react';
import {
  Button,
  Form,
  Input,
  Select,
  message,
  Row,
  Col,
  Typography,
  Spin,
  Tooltip,
} from 'antd';
import { EditOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { axiosSecureInstance } from '../../api/axios';
import { getAllIndustryType } from '../../api/industryType';
import { getUserID } from '../../Utility/service';
import './IndustrySettingsStyle.scss';

const { Title, Text } = Typography;
const { Option } = Select;

function IndustrySettings() {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [industryOptions, setIndustryOptions] = useState([]);

  useEffect(() => {
    const initialize = async () => {
      try {
        // Get industry types
        const res = await getAllIndustryType(setIndustryOptions);
        if (res.success && Array.isArray(res.data)) {
          setIndustryOptions(res.data);
        } else {
          message.error(res.message || 'Failed to load industries');
        }

        const userId = getUserID();
        if (!userId) {
          message.error('User ID not found');
          return;
        }

        const companyRes = await axiosSecureInstance.get(`api/company/getdata/${userId}`);
        const companyData = companyRes.data;

        if (!companyData || companyData.companyData === null) {
          message.error('Company data not found');
        } else {
          form.setFieldsValue({
            name: companyData.name,
            industry: companyData.industry,
          });
        }
      } catch (error) {
        console.error('Error during initialization:', error);
        message.error('Failed to load organization settings');
      } finally {
        setLoading(false);
      }
    };

    initialize();
  }, [form]);

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleSave = async (values) => {
    setSaving(true);
    try {
      const userId = getUserID();
      if (!userId) {
        message.error('User ID not found');
        return;
      }

      const saveRes = await axiosSecureInstance.patch(`api/company/update/${userId}`, values);
      message.success('Organization info updated successfully');
      setIsEditing(false);
    } catch (error) {
      console.error('Error during save:', error);
      message.error('Failed to update organization info');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="company-info-container" style={{ padding: '24px', borderRadius: '8px' }}>
      <div className="header-section" style={{ marginBottom: '24px' }}>
        <Title level={4} className="component-title">
          <InfoCircleOutlined style={{ marginRight: '8px' }} />
          Organization Settings
        </Title>
        <Text className="component-subtitle" type="secondary">
          Configure your organization details to personalize your experience
        </Text>
      </div>

      {loading ? (
        <Spin />
      ) : (
        <Form form={form} layout="vertical" onFinish={handleSave}>
          <Row gutter={[24, 24]}>
            <Col span={12}>
              <Form.Item
                label={
                  <label style={{ display: 'flex', alignItems: 'center' }}>
                    Company Name
                    <Tooltip title="Update your official company name">
                      <InfoCircleOutlined style={{ marginLeft: '8px' }} />
                    </Tooltip>
                  </label>
                }
                name="name"
                rules={[{ required: true, message: 'Please enter the company name' }]}
              >
                <Input
                  placeholder="Enter your company name"
                  style={{ borderRadius: '4px' }}
                  disabled={!isEditing}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label={
                  <label style={{ display: 'flex', alignItems: 'center' }}>
                    Industry
                    <Tooltip title celebration="Update the industry that best represents your company">
                      <InfoCircleOutlined style={{ marginLeft: '8px' }} />
                    </Tooltip>
                  </label>
                }
                name="industry"
                rules={[{ required: true, message: 'Please select an industry' }]}
              >
                <Select
                  placeholder="Select industry"
                  style={{ borderRadius: '4px' }}
                  disabled={!isEditing}
                >
                  {industryOptions.map((industry) => (
                    <Option key={industry._id} value={industry.name}>
                      {industry.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <div
            className="action-buttons-container"
            style={{ marginTop: '24px', display: 'flex', justifyContent: 'flex-start', gap: '10px' }}
          >
            {!isEditing ? (
              <Tooltip title="Edit organization settings">
                <Button
                  type="primary"
                  onClick={handleEdit}
                  icon={<EditOutlined />}
                  style={{ borderRadius: '4px', minWidth: '120px' }}
                >
                  Edit
                </Button>
              </Tooltip>
            ) : (
              <Tooltip title="Save organization settings">
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={saving}
                  icon={<EditOutlined />}
                  style={{ borderRadius: '4px', minWidth: '120px' }}
                >
                  Save Changes
                </Button>
              </Tooltip>
            )}
          </div>
        </Form>
      )}
    </div>
  );
}

export default IndustrySettings;